// src/api/ticketAssignmentService.js
// -----------------------------------------------------------------------------
// Service managing Ticket Assignment operations (Ticket_Assignment_TBL).
// Connects to /TicketAssignment endpoints with resilient fallbacks.
// -----------------------------------------------------------------------------
import axiosClient from './axiosClient';
import { TicketAssignmentModel } from '../Models/ticketAssignmentModel';

const ticketAssignmentService = {
  getAll: () => {
    return axiosClient.get('/TicketAssignment')
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn('GET /TicketAssignment returned 404. Falling back to GET /ticket-assignments...');
          return axiosClient.get('/ticket-assignments');
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const rawList = res?.data?.data || res?.data || [];
        const mappedList = (Array.isArray(rawList) ? rawList : []).map(TicketAssignmentModel.toFrontend);
        return {
          data: mappedList,
        };
      });
  },

  getByTicketId: (ticketID) => {
    return axiosClient.get('/TicketAssignment')
      .catch(() => axiosClient.get(`/tickets/${ticketID}/assignments`))
      .catch(() => axiosClient.get('/ticket-assignments'))
      .then((res) => {
        let rawList = res?.data?.data || res?.data || [];
        if (!Array.isArray(rawList)) {
          rawList = rawList ? [rawList] : [];
        }
        const filtered = rawList
          .filter((a) => a && Number(a.ticketId || a.ticketID || a.TicketId || a.TicketID) === Number(ticketID))
          .map(TicketAssignmentModel.toFrontend);
        return { data: filtered };
      })
      .catch(() => ({ data: [] }));
  },

  getById: (assignmentID) => {
    return axiosClient.get(`/TicketAssignment/${assignmentID}`)
      .catch((err) => {
        if (err?.response?.status === 404) {
          return axiosClient.get(`/ticket-assignments/${assignmentID}`);
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        return {
          data: TicketAssignmentModel.toFrontend(raw),
        };
      });
  },

  create: async (payload) => {
    const body = TicketAssignmentModel.toBackend(payload);
    const ticketID = Number(body.ticketId || payload.ticketId || payload.ticketID || 0);
    const userID = Number(body.userId || payload.userId || payload.userID || 0);
    const assignedBy = Number(body.assignedBy || payload.assignedBy || 1);
    const nowIso = new Date().toISOString();

    const requestBody = {
      assignmentId: 0,
      ticketId: ticketID,
      userId: userID,
      assignedBy: assignedBy,
      assignedDttm: body.assignedDttm || nowIso,
      isCurrentFlag: true,
    };

    if (body.unassignedDttm) {
      requestBody.unassignedDttm = body.unassignedDttm;
    }

    // Try standard POST /TicketAssignment
    try {
      const res = await axiosClient.post('/TicketAssignment', requestBody);
      const raw = res?.data?.data || res?.data;
      return { data: TicketAssignmentModel.toFrontend(raw) };
    } catch (err) {
      console.warn('POST /TicketAssignment failed, attempting retry without assignmentId...', err);

      // Retry without assignmentId
      try {
        const cleanBody = {
          ticketId: ticketID,
          userId: userID,
          assignedBy: assignedBy,
          assignedDttm: body.assignedDttm || nowIso,
          isCurrentFlag: true,
        };
        const res = await axiosClient.post('/TicketAssignment', cleanBody);
        const raw = res?.data?.data || res?.data;
        return { data: TicketAssignmentModel.toFrontend(raw) };
      } catch (retryErr) {
        console.warn('Retry without assignmentId failed:', retryErr);
      }

      // Fallback: Try /ticket-assignments
      try {
        const res = await axiosClient.post('/ticket-assignments', requestBody);
        const raw = res?.data?.data || res?.data;
        return { data: TicketAssignmentModel.toFrontend(raw) };
      } catch (fErr2) {
        console.warn('Fallback /ticket-assignments failed:', fErr2);
      }

      return Promise.reject(err);
    }
  },

  update: (assignmentID, payload) => {
    const body = TicketAssignmentModel.toBackend({ ...payload, assignmentID });

    return axiosClient.put(`/TicketAssignment/${assignmentID}`, body)
      .catch((err) => {
        if (err?.response?.status === 404 || err?.response?.status === 405) {
          return axiosClient.put('/TicketAssignment', body);
        }
        return Promise.reject(err);
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          return axiosClient.put(`/ticket-assignments/${assignmentID}`, body);
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        return {
          data: TicketAssignmentModel.toFrontend(raw),
        };
      });
  },

  remove: (assignmentID) => {
    return axiosClient.delete(`/TicketAssignment/${assignmentID}`)
      .catch((err) => {
        if (err?.response?.status === 404) {
          return axiosClient.delete(`/ticket-assignments/${assignmentID}`);
        }
        return Promise.reject(err);
      })
      .catch(() => {
        // Fallback: Soft unassign via PUT
        return axiosClient.put(`/TicketAssignment/${assignmentID}`, {
          assignmentId: Number(assignmentID),
          isCurrentFlag: false,
          unassignedDttm: new Date().toISOString(),
        });
      });
  },
};

export default ticketAssignmentService;

